import React, {useEffect, useRef} from 'react'
import {useState} from 'react'
import './GettingStarted.scss'
import {Form, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useI18n} from '../../../../../Utils/_helpers/useI18n'
import {STEP_KEYS} from '../../../../../Utils/Constant/WalletCard'
import { TEMPLATE_NAME_TOOLTIP } from '../../../wallet-card-templates/constants'
import { useGetWalletCardTemplateListSelectQuery } from '../../../../../../services/cellcast.v2'

interface GettingStartedProps {
  cardData: any
  isTemplate?: boolean
  handleChange: (e: any) => void
  handleBlur: (e: any) => void
  touched: any
  errors: any
  MAX_LENGTH: number
  disableInput: boolean
  isCompleted?: boolean
  setIsCompleted?: (completed: boolean) => void
  isExpanded?: boolean
  onExpand?: (step: string) => void
  setWalletCardTemplateID: (id: string) => void
  walletCardTemplateID: string
  templateOptions: any[]
}

const GettingStarted: React.FC<GettingStartedProps> = ({
  cardData,
  isTemplate = false,
  handleChange,
  handleBlur,
  touched,
  errors,
  MAX_LENGTH,
  disableInput,
  isCompleted = false,
  setIsCompleted,
  isExpanded = true,
  onExpand,
  setWalletCardTemplateID,
  walletCardTemplateID,
  templateOptions,
}) => {
  const {t} = useI18n()
  const [isContentVisible, setIsContentVisible] = useState(isExpanded)
  const [localTouched, setLocalTouched] = useState({
    cardName: false,
    organizationName: false,
  })

  const [localErrors, setLocalErrors] = useState({
    cardName: '',
    organizationName: '',
  })


  useEffect(() => {
    setIsContentVisible(isExpanded)
  }, [isExpanded])

  // Validate field
  const validateField = (name: string, value: string) => {
    let error = ''
    const trimmedValue = value.trim()

    if (!trimmedValue) {
      error = t('WALLET_CARD.VALIDATION.REQUIRED', {
        name: t('WALLET_CARD.FORM.CARD_NAME'),
      })
    } else if (trimmedValue.length > MAX_LENGTH) {
      error = t('WALLET_CARD.VALIDATION.MAX_LENGTH', {
        name: t('WALLET_CARD.FORM.CARD_NAME'),
        maxLength: MAX_LENGTH,
      })
    }

    return error
  }

  // Handle local field changes
  const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target
    setLocalTouched((prev) => ({
      ...prev,
      [name]: true,
    }))

    // Validate field
    const error = validateField(name, value)
    setLocalErrors((prev) => ({
      ...prev,
      [name]: error,
    }))

    handleChange(e)
  }

  // Handle local field blur
  const handleLocalBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const {name, value} = e.target
    setLocalTouched((prev) => ({
      ...prev,
      [name]: true,
    }))

    // Validate field
    const error = validateField(name, value)
    setLocalErrors((prev) => ({
      ...prev,
      [name]: error,
    }))

    handleBlur(e)
  }

  const handleTemplateSelect: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
    setWalletCardTemplateID?.(e.target.value)
    const syntheticEvt = {
      target: { name: 'template', value: e.target.value },
    } as unknown as React.ChangeEvent<HTMLInputElement>
    handleLocalChange(syntheticEvt)
  }

  useEffect(() => {
    if (setIsCompleted) {
      // Check if required fields have values
      const hasCardName = cardData.cardName && cardData.cardName.trim() !== ''

      // For non-template cards, ensure a template is selected
      const hasTemplateSelected = isTemplate
        ? true
        : walletCardTemplateID && walletCardTemplateID.trim() !== ''

      // Check if fields have no local errors
      const hasNoErrors = !localErrors.cardName

      // Form is valid if all required fields have values, no errors, and (when applicable) a template is selected
      const isFormValid = hasCardName && hasNoErrors && hasTemplateSelected

      setIsCompleted(isFormValid)
    }
  }, [cardData.cardName, localErrors, isTemplate, walletCardTemplateID, setIsCompleted])

  const toggleContent = () => {
    const newState = !isContentVisible
    setIsContentVisible(newState)
    if (newState && onExpand) {
      onExpand(STEP_KEYS.GETTING_STARTED)
    }
  }

  return (
    <div
      className={`wizard-step getting-started-wallet-card getting-started ${
        isExpanded ? 'border-primary' : ''
      } border bg-white`}
    >
      <div
        className={`wizard-step-header cursor-pointer ${
          isContentVisible ? 'border-radius-bottom-0' : 'border-radius-bottom'
        }`}
        onClick={toggleContent}
      >
        <div className='step-indicator'>
          <div
            className={`step-number ${
              isCompleted ? 'completed border-primary' : 'border-secondary'
            } `}
          >
            <span></span>
            {isCompleted && (
              <svg
                className='check-icon'
                width='12'
                height='9'
                viewBox='0 0 12 9'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M10.6668 1L4.00016 7.66667L1.3335 5'
                  stroke='currentColor'
                  strokeWidth='1.67'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            )}
          </div>
          <div className='step-title d-flex flex-column'>
            <h2 className={`${isExpanded ? 'text-primary' : 'text-dark'} fw-semibold fs-2`}>
              {t('WALLET_CARD.GETTING_STARTED.TITLE')}
            </h2>
            <span>{t('WALLET_CARD.GETTING_STARTED.SUBTITLE', {card: 'B-Card'})}</span>
          </div>
          <h2 className={`${isExpanded ? 'text-primary' : 'text-muted'} fw-semibold fs-2 text-number-step`}>1</h2>
          <div className={`collapse-arrow ${!isContentVisible ? 'collapsed' : ''}`}>
            <svg
              width='10'
              height='6'
              viewBox='0 0 10 6'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M1 1L5 5L9 1'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </div>
      </div>

      <div className={`wizard-step-content ${!isContentVisible ? 'd-none' : 'p-4'}`}>
        <div className='campaign-form'>
          {!isTemplate && (
            <Form.Group className='mb-4'>
              <Form.Label className='form-label d-flex gap-1 align-items-center'>
                {t('WALLET_CARD.GETTING_STARTED.SUBTITLE_TEMPLATE')} <span className='text-danger'>*</span> 
              </Form.Label>
              <Form.Select
                name='template'
                value={walletCardTemplateID}
                onChange={handleTemplateSelect}
                disabled={disableInput || templateOptions.length === 0}
              >
                {templateOptions.map((tpl: any) => (
                  <option key={tpl.id} value={tpl.id}>
                    {tpl.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          <Form.Group className='mb-4'>
            <Form.Label className='form-label d-flex gap-1 align-items-center'>
              {isTemplate
                ? t('MOBILE_WALLET_CARD.FORM.TEMPLATE_NAME')
                : t('WALLET_CARD.FORM.CARD_NAME')}

              {isTemplate && (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    <Tooltip id='template-name-tooltip'>
                      {t('MOBILE_WALLET_CARD.FORM.TEMPLATE_NAME_TOOLTIP', {
                        defaultValue:TEMPLATE_NAME_TOOLTIP  
                      })}
                    </Tooltip>
                  }
                >
                  <i className='bi bi-info-circle-fill text-gray-600 cursor-pointer ms-1'></i>
                </OverlayTrigger>
              )}
              <span className='text-danger'>*</span>
            </Form.Label>
            <Form.Control
              type='text'
              name='cardName'
              value={cardData.cardName}
              onChange={handleLocalChange}
              onBlur={handleLocalBlur}
              placeholder={t('WALLET_CARD.PLACEHOLDER.CARD_NAME')}
              required
              isInvalid={localTouched.cardName && !!localErrors.cardName}
              maxLength={MAX_LENGTH}
              disabled={disableInput}
              className='campaign-input'
            />
            {localTouched.cardName && localErrors.cardName && (
              <div className='fv-plugins-message-container'>
                <span role='alert' className='text-danger'>
                  {localErrors.cardName}
                </span>
              </div>
            )}
          </Form.Group>
        </div>
      </div>
    </div>
  )
}

export default GettingStarted
